import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { useCurrency as useCurrencyLoader } from './hooks/useCurrency';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { BillingPage } from './pages/billing/BillingPage';
import { MainLayout } from './layouts/MainLayout';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { PosPage } from './pages/pos/PosPage';
import { PosShiftsPage } from './pages/pos/PosShiftsPage';
import { PosSalesPage } from './pages/pos/PosSalesPage';
import { PosReturnsPage } from './pages/pos/PosReturnsPage';
import { GiftCardsPage } from './pages/pos/GiftCardsPage';
import { CustomerDisplayPage } from './pages/pos/CustomerDisplayPage';
import { ProductsPage } from './pages/inventory/ProductsPage';
import { CatalogSetupPage } from './pages/inventory/CatalogSetupPage';
import { ProductDetailPage } from './pages/inventory/ProductDetailPage';
import { StockPage } from './pages/inventory/StockPage';
import { AdjustmentsPage } from './pages/inventory/AdjustmentsPage';
import { TransfersPage } from './pages/inventory/TransfersPage';
import { LocationsPage } from './pages/inventory/LocationsPage';
import { StockCountsPage } from './pages/inventory/StockCountsPage';
import { ReorderSuggestionsPage } from './pages/inventory/ReorderSuggestionsPage';
import { SupplierReturnsPage } from './pages/inventory/SupplierReturnsPage';
import { VariantsPage } from './pages/inventory/VariantsPage';
import { SerialNumbersPage } from './pages/inventory/SerialNumbersPage';
import { ExpiringBatchesPage } from './pages/inventory/ExpiringBatchesPage';
import { BulkIoPage } from './pages/inventory/BulkIoPage';
import { ChartOfAccountsPage } from './pages/accounting/ChartOfAccountsPage';
import { JournalEntriesPage } from './pages/accounting/JournalEntriesPage';
import { VouchersPage } from './pages/accounting/VouchersPage';
import { ReportsPage } from './pages/accounting/ReportsPage';
import { InvoicesPage } from './pages/accounting/InvoicesPage';
import { ArAgingPage } from './pages/accounting/ArAgingPage';
import { VendorInvoicesPage } from './pages/accounting/VendorInvoicesPage';
import { ApAgingPage } from './pages/accounting/ApAgingPage';
import { PaymentRunsPage } from './pages/accounting/PaymentRunsPage';
import { PeriodClosePage } from './pages/accounting/PeriodClosePage';
import { BankReconciliationPage } from './pages/accounting/BankReconciliationPage';
import { FxPage } from './pages/accounting/FxPage';
import { TaxPage } from './pages/accounting/TaxPage';
import { CustomersPage } from './pages/sales/CustomersPage';
import { OrdersPage } from './pages/sales/OrdersPage';
import { QuotesPage } from './pages/sales/QuotesPage';
import { CustomerDetailPage } from './pages/sales/CustomerDetailPage';
import { SegmentsPage } from './pages/sales/SegmentsPage';
import { LeadsPage } from './pages/sales/LeadsPage';
import { PipelinePage } from './pages/sales/PipelinePage';
import { PromotionsPage } from './pages/sales/PromotionsPage';
import { CommissionsPage } from './pages/sales/CommissionsPage';
import { SuppliersPage } from './pages/purchase/SuppliersPage';
import { PurchaseOrdersPage } from './pages/purchase/PurchaseOrdersPage';
import { GrnsPage } from './pages/purchase/GrnsPage';
import { RtvPage } from './pages/purchase/RtvPage';
import { RequisitionsPage } from './pages/purchase/RequisitionsPage';
import { RfqPage } from './pages/purchase/RfqPage';
import { SupplierScorecardPage, SupplierOnboardingPage } from './pages/purchase/SupplierLifecyclePage';
import { EmployeesPage } from './pages/hrm/EmployeesPage';
import { PayrollPage } from './pages/hrm/PayrollPage';
import { AttendancePage } from './pages/hrm/AttendancePage';
import { LeavePage } from './pages/hrm/LeavePage';
import { PerformancePage } from './pages/hrm/PerformancePage';
import { RecruitmentPage } from './pages/hrm/RecruitmentPage';
import { TrainingPage } from './pages/hrm/TrainingPage';
import { StorefrontsPage } from './pages/ecommerce/StorefrontsPage';
import { ReviewsPage } from './pages/ecommerce/ReviewsPage';
import { CartsPage } from './pages/ecommerce/CartsPage';
import { IntegrationsPage } from './pages/ecommerce/IntegrationsPage';
import { CampaignsPage } from './pages/ecommerce/CampaignsPage';
import { UsersPage } from './pages/admin/UsersPage';
import { RolesPage } from './pages/admin/RolesPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { BranchesPage } from './pages/admin/BranchesPage';
import { AuditLogPage } from './pages/admin/AuditLogPage';
import { AdminPlatformPage } from './pages/admin/AdminPlatformPage';
import { PlanManagementPage } from './pages/admin/PlanManagementPage';
import { ReportsIndexPage } from './pages/reports/ReportsIndexPage';
import { SalesInventoryReportsPage } from './pages/reports/SalesInventoryReportsPage';
import { FinanceHrReportsPage } from './pages/reports/FinanceHrReportsPage';
import { ExportsSchedulesPage } from './pages/reports/ExportsSchedulesPage';
import { NotFoundPage } from './pages/NotFoundPage';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.accessToken);
  // Hydrate global currency cache for all authenticated pages
  useCurrencyLoader();
  if (!user || !token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/pos/customer-display/:tenantId/:terminalCode" element={<CustomerDisplayPage />} />

      <Route
        path="/"
        element={
          <RequireAuth>
            <MainLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="billing" element={<BillingPage />} />

        <Route path="pos" element={<PosPage />} />
        <Route path="pos/shifts" element={<PosShiftsPage />} />
        <Route path="pos/sales" element={<PosSalesPage />} />
        <Route path="pos/returns" element={<PosReturnsPage />} />
        <Route path="pos/gift-cards" element={<GiftCardsPage />} />

        <Route path="inventory/products" element={<ProductsPage />} />
        <Route path="inventory/catalog-setup" element={<CatalogSetupPage />} />
        <Route path="inventory/products/:id" element={<ProductDetailPage />} />
        <Route path="inventory/stock" element={<StockPage />} />
        <Route path="inventory/adjustments" element={<AdjustmentsPage />} />
        <Route path="inventory/transfers" element={<TransfersPage />} />
        <Route path="inventory/locations" element={<LocationsPage />} />
        <Route path="inventory/counts" element={<StockCountsPage />} />
        <Route path="inventory/reorder" element={<ReorderSuggestionsPage />} />
        <Route path="inventory/rma" element={<SupplierReturnsPage />} />
        <Route path="inventory/variants" element={<VariantsPage />} />
        <Route path="inventory/serials" element={<SerialNumbersPage />} />
        <Route path="inventory/expiring" element={<ExpiringBatchesPage />} />
        <Route path="inventory/bulk" element={<BulkIoPage />} />

        <Route path="accounting/accounts" element={<ChartOfAccountsPage />} />
        <Route path="accounting/vouchers" element={<VouchersPage />} />
        <Route path="accounting/journal-entries" element={<JournalEntriesPage />} />
        <Route path="accounting/reports" element={<ReportsPage />} />
        <Route path="accounting/ar/invoices" element={<InvoicesPage />} />
        <Route path="accounting/ar/aging" element={<ArAgingPage />} />
        <Route path="accounting/ap/invoices" element={<VendorInvoicesPage />} />
        <Route path="accounting/ap/aging" element={<ApAgingPage />} />
        <Route path="accounting/ap/payment-runs" element={<PaymentRunsPage />} />
        <Route path="accounting/period-close" element={<PeriodClosePage />} />
        <Route path="accounting/bank-rec" element={<BankReconciliationPage />} />
        <Route path="accounting/fx" element={<FxPage />} />
        <Route path="accounting/tax" element={<TaxPage />} />

        <Route path="sales/customers" element={<CustomersPage />} />
        <Route path="sales/orders" element={<OrdersPage />} />
        <Route path="sales/quotes" element={<QuotesPage />} />
        <Route path="sales/customers/:id" element={<CustomerDetailPage />} />
        <Route path="sales/segments" element={<SegmentsPage />} />
        <Route path="sales/leads" element={<LeadsPage />} />
        <Route path="sales/pipeline" element={<PipelinePage />} />
        <Route path="sales/promotions" element={<PromotionsPage />} />
        <Route path="sales/commissions" element={<CommissionsPage />} />

        <Route path="purchase/suppliers" element={<SuppliersPage />} />
        <Route path="purchase/orders" element={<PurchaseOrdersPage />} />
        <Route path="purchase/grns" element={<GrnsPage />} />
        <Route path="purchase/rtv" element={<RtvPage />} />
        <Route path="purchase/requisitions" element={<RequisitionsPage />} />
        <Route path="purchase/rfqs" element={<RfqPage />} />
        <Route path="purchase/scorecard" element={<SupplierScorecardPage />} />
        <Route path="purchase/onboarding" element={<SupplierOnboardingPage />} />

        <Route path="hrm/employees" element={<EmployeesPage />} />
        <Route path="hrm/attendance" element={<AttendancePage />} />
        <Route path="hrm/leave" element={<LeavePage />} />
        <Route path="hrm/performance" element={<PerformancePage />} />
        <Route path="hrm/recruitment" element={<RecruitmentPage />} />
        <Route path="hrm/training" element={<TrainingPage />} />
        <Route path="hrm/payroll" element={<PayrollPage />} />

        <Route path="ecommerce/storefronts" element={<StorefrontsPage />} />
        <Route path="ecommerce/carts" element={<CartsPage />} />
        <Route path="ecommerce/integrations" element={<IntegrationsPage />} />
        <Route path="ecommerce/campaigns" element={<CampaignsPage />} />
        <Route path="ecommerce/reviews" element={<ReviewsPage />} />

        <Route path="reports" element={<ReportsIndexPage />} />
        <Route path="reports/sales-inventory" element={<SalesInventoryReportsPage />} />
        <Route path="reports/finance-hr" element={<FinanceHrReportsPage />} />
        <Route path="reports/exports" element={<ExportsSchedulesPage />} />

        <Route path="admin/users" element={<UsersPage />} />
        <Route path="admin/roles" element={<RolesPage />} />
        <Route path="admin/branches" element={<BranchesPage />} />
        <Route path="admin/settings" element={<SettingsPage />} />
        <Route path="admin/audit-log" element={<AuditLogPage />} />
        <Route path="admin/platform" element={<AdminPlatformPage />} />
        <Route path="admin/plans" element={<PlanManagementPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
